import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBdsLanguage } from '@/core/hooks/useBdsLanguage';
import { ValidationFeedback } from '@/core/components/ValidationFeedback/ValidationFeedback';
import { ImageEditButton } from './ImageEditor/ImageEditButton';
import { ImageEditor } from './ImageEditor/ImageEditor';
import { getStoriesUploadConfig } from './uploadConfigs';
import { useUploadHandler } from './useUploadHandler';
import { ValidationResult } from '../../utils/mediaValidation';

interface StoryContentProps {
  onValidationChange?: (isValid: boolean, file: File | null, validation: ValidationResult | null) => void;
  onTagsChange?: (tags: string[]) => void;
}

export const StoryContent: React.FC<StoryContentProps> = ({
  onValidationChange,
  onTagsChange,
}) => {
  const { t } = useTranslation();
  const bdsLanguage = useBdsLanguage();
  const {
    validationResult,
    selectedFile,
    editSelectedImage,
    pendingImageFile,
    selectedImagePreviewUrl,
    confirmImage,
    imageContext,
    feedbackFileName,
    isValidating,
    setupUploadListeners
  } = useUploadHandler({
    publicationType: 'stories',
    onValidationChange
  });

  const showValidImagePreview = validationResult?.isValid === true &&
    Boolean(selectedImagePreviewUrl) &&
    selectedFile?.type.toLowerCase().startsWith('image/') === true;

  const uploadConfig = getStoriesUploadConfig(t);

  useEffect(() => {
    const inputChips = document.getElementById('stories-tags-chips');
    if (inputChips) {
      const handleChangeChips = (event: any) => {
        const chips = event.detail || [];
        onTagsChange?.(chips);
      };

      const handleSubmit = (event: any) => {
        onTagsChange?.([event.detail]);
      };

      inputChips.addEventListener('bdsChangeChips', handleChangeChips);
      inputChips.addEventListener('bdsSubmit', handleSubmit);

      return () => {
        inputChips.removeEventListener('bdsChangeChips', handleChangeChips);
        inputChips.removeEventListener('bdsSubmit', handleSubmit);
      };
    }
  }, [onTagsChange]);

  useEffect(() => {
    const listeners = setupUploadListeners();
    
    return () => {
      listeners?.cleanup();
    };
  }, [setupUploadListeners]);

  return (
    <>
      <bds-input-chips 
        id="stories-tags-chips"
        label={t('storiesTags')}
        placeholder={t('storiesTagsPlaceholder')}
        chips="[]"
      />
      
      <bds-upload
        id="upload-stories"
        accept={uploadConfig.accept}
        multiple={false}
        language={bdsLanguage}
        subtitle={uploadConfig.subtitle}
        title-name={uploadConfig.titleName}
        max-size={uploadConfig.maxSize}
      />
      
      {pendingImageFile && (
        <ImageEditor sourceFile={pendingImageFile} onConfirm={confirmImage} imageContext={imageContext} />
      )}
      {!pendingImageFile && showValidImagePreview && selectedImagePreviewUrl && (
        <div className="scheduled-image-editor_preview">
          <img src={selectedImagePreviewUrl} alt={t('upload.selectedImagePreviewAlt')} />
          <div className="scheduled-image-editor_preview-actions">
            <ImageEditButton label={t('upload.editImage')} onEdit={editSelectedImage} />
          </div>
        </div>
      )}
      {!pendingImageFile && !showValidImagePreview && !isValidating && (
        <ValidationFeedback
          validationResult={validationResult}
          fileName={feedbackFileName ?? undefined}
        />
      )}
    </>
  );
}; 